// Each permission is a distinct bit. Add new perms as the next power of two.
// Only the four marked (*) are wired to routes in this slice; the rest are
// reserved for later slices so the catalog is stable.
export const PERMS = {
  TENANTS_READ: 1n << 0n, // *
  TENANT_NOTES_WRITE: 1n << 1n, // *
  TENANT_STATUS_WRITE: 1n << 2n, // *
  ENTITLEMENTS_READ: 1n << 3n, // *
  ENTITLEMENTS_WRITE: 1n << 4n, // reserved (SP2)
  USERS_READ: 1n << 5n, // reserved (SP3)
  USERS_MANAGE: 1n << 6n, // reserved (SP3)
  STAFF_READ: 1n << 7n, // reserved (SP4)
  STAFF_MANAGE: 1n << 8n, // reserved (SP4)
  ROLES_MANAGE: 1n << 9n, // reserved (SP4)
  BUSINESS_TYPES_READ: 1n << 10n, // *
  BUSINESS_TYPES_WRITE: 1n << 11n, // * — create/edit types, edit + publish template versions (Slice 2)
  ASSISTANT_REVIEW: 1n << 12n, // * — read escalations + submit review_log annotations (PR9)
  ASSISTANT_CURATE: 1n << 13n, // reserved (PR10) — knowledge editor
  ASSISTANT_RESOLVE: 1n << 14n, // reserved (PR11) — escalation live chat + resolve
  COST_PLUS_READ: 1n << 15n, // * — read messaging supplier catalog, buy rates, tenant plans + margin
  COST_PLUS_WRITE: 1n << 16n, // * — edit suppliers, buy rates (COGS), and per-tenant plans (Root-internal)
  ONBOARDING_VIEW: 1n << 17n, // CS: read queue and per-tenant state
  ONBOARDING_AUDIT_VIEW: 1n << 18n, // CS: read event log + inline audit preview
  ONBOARDING_CLAIM: 1n << 19n, // CS: claim, renew, release
  ONBOARDING_OVERRIDE_CLAIM: 1n << 20n, // CS lead: release another admin's active claim
  ONBOARDING_SNOOZE: 1n << 21n, // CS: snooze and unsnooze
  ONBOARDING_MARK_LIVE: 1n << 22n, // CS lead: call markGoLive() on tenant's behalf
  ONBOARDING_RESCUE_EMAIL: 1n << 23n, // CS: manually trigger rescue email
  ACCOUNTING_MODULES_READ: 1n << 24n, // Finance: read accounting modules (cash/accrual)
  ACCOUNTING_MODULES_WRITE: 1n << 25n, // Finance: write accounting modules (cash/accrual)
  FEEDBACK_REVIEW: 1n << 26n, // Support: read/list + open the feedback triage inbox
  FEEDBACK_RESOLVE: 1n << 27n, // Support: change status + edit internal notes
  ENTITLEMENTS_LOCK: 1n << 28n, // * — set/clear an entitlement's 'locked' mode (platform-admin scope; engine gate)
  ENTITLEMENTS_PROMOTIONAL: 1n << 29n, // reserved — grant source='promotional' (bulk/promotional flow, A4-rest)
  ONBOARDING_RESYNC: 1n << 30n, // reserved (MEGA-5 piece C) -- CS: trigger a tenant onboarding resync scan (read-triggering diagnostic, non-mutating to step state)
  ONBOARDING_STEP_OVERRIDE: 1n << 31n, // reserved (MEGA-5 piece C) -- CS: force-complete or reopen an onboarding step (mutates authoritative step state)
  ONBOARDING_PRODUCTION_APPLICATION_REVIEW: 1n << 32n, // CS: record CardPointe merchant-application approval/decline (mutates authoritative step state; distinct from ONBOARDING_STEP_OVERRIDE because this records a real business outcome, not a generic override)
  // ---- V1 parity port (spec 2026-08-23-admin-v1-parity-design.md §4) ----
  PRICING_READ: 1n << 33n, // Pricing: read plans, module pricing, tenant subscriptions
  PRICING_WRITE: 1n << 34n, // Pricing: edit plans + module add-on pricing
  SUBSCRIPTIONS_WRITE: 1n << 35n, // Pricing: assign/change a tenant's plan + seat count
  TENANT_ORG_WRITE: 1n << 36n, // Tenant: create/edit locations, profit centers, terminals
  TENANT_USERS_MANAGE: 1n << 37n, // Tenant: create/edit/deactivate tenant users + tenant roles
  TENANTS_CREATE: 1n << 38n, // Tenant: provision a new tenant (critical tier)
  FEEDBACK_TRIAGE: 1n << 39n, // Support: impact/effort scoring, bulk status, CSV export
  FEEDBACK_ERASE: 1n << 40n, // Support: permanently erase a feedback request on a verified privacy request (#14561)
  // ---- E-3 L3 Worker page (P2-H01; SQ-13) ----
  WORKER_READ: 1n << 41n, // Ops: Worker page views (schedules, dead jobs, kind state, alerts, findings)
  WORKER_OPERATE: 1n << 42n, // Ops: requeue (requeueSafe only), pause/unpause, run now, fire drill — audited
  REGULATED_PROFILE_WRITE: 1n << 43n, // Entitlements: record/correct a tenant's regulated profile (#15687) — switches the regulated-industry guards
} as const;

export type PermName = keyof typeof PERMS;

export function bitmapFor(perms: bigint[]): bigint {
  return perms.reduce((acc, p) => acc | p, 0n);
}
